import React, { memo, useCallback, useMemo } from 'react';
import {
  CheckboxListItem,
  Divider,
  HStack,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import { CheckboxFilterSection, CheckboxFilterItem } from '../DynamicTable';
import i18n from '../../../utilities/i18n';
import { useAppSelector } from '../../../reducers/hooks';

export type CheckboxFilterInput = {
  id: string | number;
  value: boolean;
};

type CheckboxFilterListProps = {
  id: string | number;
  label: string;
  items: CheckboxFilterItem[];
  searchText: string;
  onChange: (id: CheckboxFilterSection['id'], item: CheckboxFilterItem) => void;
};

/**
 * Displays a list of checkbox filters.
 * @param {string | number} id - The ID of the filter section, used for updating state.
 * @param {string} label - The label of the filter section.
 * @param {CheckboxFilterItem[]} items - The list of checkbox filter items.
 * @param {string} searchText - The current search text to filter the items by.
 * @param {function} onChange - The function to call when a checkbox is changed.
 * @returns {React.JSX.Element}
 */
function CheckboxFilterList({
  id,
  label,
  items,
  searchText,
  onChange,
}: CheckboxFilterListProps) {
  const { language } = useAppSelector((state) => state.user);

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) =>
        a.label.localeCompare(b.label, language, {
          numeric: true,
          sensitivity: 'base',
        }),
      ),
    [items, language],
  );

  const normalizedSearchText = useMemo(
    () => searchText.trim().toLowerCase(),
    [searchText],
  );

  const { selectedCount, filteredItems } = useMemo(() => {
    let count = 0;
    const nextFilteredItems: CheckboxFilterItem[] = [];

    for (const item of sortedItems) {
      if (item.value) {
        count += 1;
      }

      if (
        !normalizedSearchText ||
        item.label.toLowerCase().includes(normalizedSearchText)
      ) {
        nextFilteredItems.push(item);
      }
    }

    return {
      selectedCount: count,
      filteredItems: nextFilteredItems,
    };
  }, [sortedItems, normalizedSearchText]);

  const handleItemChange = useCallback(
    (item: CheckboxFilterItem, checked: boolean) => {
      onChange(id, { ...item, value: checked });
    },
    [id, onChange],
  );

  const renderCheckboxFilterItems = () => {
    // there are no items at all, even before filtering
    if (sortedItems.length === 0) {
      return (
        <Text type={'$bodyBase.default'} padding={'$2'}>
          {i18n.t('Common.noData')}
        </Text>
      );
    }

    // there are items but none match the search filter
    if (filteredItems.length === 0) {
      return (
        <Text type={'$bodyBase.default'} padding={'$2'}>
          {i18n.t('Filters.noResults')}
        </Text>
      );
    }

    return filteredItems.map((item) => (
      <CheckboxListItem
        key={item.id}
        testID={`checkbox-filter-${id}-${item.id}`}
        title={item.label}
        checked={item.value}
        onPress={(checked) => handleItemChange(item, checked)}
      />
    ));
  };

  return (
    <VStack width="100%" testID={`checkbox-filter-section-${id}`}>
      <HStack gap={'$2'} paddingBottom={'$2'} alignItems="center">
        <Text
          type={'$bodyBase.strong'}
          testID={`checkbox-filter-section-${id}-label`}
        >
          {label}
        </Text>
        {selectedCount ? (
          <Text type={'$bodyBase.default'}>({selectedCount})</Text>
        ) : null}
      </HStack>
      <Divider />
      {renderCheckboxFilterItems()}
    </VStack>
  );
}

export default memo(CheckboxFilterList);
